'use client';

import { motion, useReducedMotion, type Variants } from 'framer-motion';
import { useLocale, useTranslations } from 'next-intl';
import { ArrowRight } from 'lucide-react';
import { Link } from '@/navigation';
import { SmartImage } from '@/components/ui/Skeleton';
import { formatPostDate, type BlogPostMeta } from '@/lib/blog';

interface BlogPostCopy {
  title: string;
  excerpt: string;
  author: string;
  readTime: string;
}

// Shared entrance choreography for post-card grids (blog list, "read also"):
// the container staggers, each card springs up, then lifts on hover. Tonal
// motion only — no shadows, per the design system.
export const postGridVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

export const postCardVariants: Variants = {
  hidden: { y: 28, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { type: 'spring', stiffness: 100, damping: 18 } },
};

// One blog-post card. Reads its own localized copy from messages by post id;
// links to the current locale's slug. Render inside a motion container that
// provides the `hidden`/`visible` variant context.
export default function BlogPostCard({ meta }: { meta: BlogPostMeta }) {
  const t = useTranslations('blog');
  const locale = useLocale();
  const reduceMotion = useReducedMotion();

  const post = t.raw(`posts.${meta.id}`) as BlogPostCopy;
  const slug = meta.slugs[locale as keyof typeof meta.slugs] ?? meta.slugs.pl;

  return (
    <motion.div
      className="h-full"
      variants={postCardVariants}
      whileHover={
        reduceMotion
          ? undefined
          : { y: -8, transition: { type: 'spring', stiffness: 300, damping: 22 } }
      }
    >
      <Link href={`/blog/${slug}`} className="group block h-full">
        <article className="forest-card flex h-full flex-col overflow-hidden transition-colors group-hover:border-primary/50">
          <div className="relative aspect-video overflow-hidden">
            <SmartImage
              src={meta.image}
              alt={post.title}
              fill
              sizes="(min-width: 1024px) 30vw, (min-width: 768px) 45vw, 92vw"
              className="object-cover transition-transform duration-500 group-hover:scale-105"
            />
          </div>
          <div className="flex flex-1 flex-col gap-stack-sm p-6">
            <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-body-sm text-on-surface-variant">
              <span className="font-medium text-primary">{post.author}</span>
              <span aria-hidden className="size-1 rounded-full bg-outline" />
              <time dateTime={meta.date}>{formatPostDate(meta.date, locale)}</time>
              <span aria-hidden className="size-1 rounded-full bg-outline" />
              <span>{post.readTime}</span>
            </p>
            <h2 className="font-headline text-headline-md font-semibold text-on-surface transition-colors group-hover:text-primary">
              {post.title}
            </h2>
            <p className="text-body-md text-on-surface-variant">{post.excerpt}</p>
            <span className="mt-auto inline-flex items-center pt-2 text-label-caps uppercase tracking-widest text-primary">
              {t('readMore')}
              <ArrowRight
                aria-hidden
                className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
              />
            </span>
          </div>
        </article>
      </Link>
    </motion.div>
  );
}
